// ============================================================
// API Client for Molancer
// Centralized HTTP client with request/response handling
// ============================================================

const API_BASE = import.meta.env.VITE_API_URL || "/api/v1";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  meta?: {
    pagination?: {
      cursor: string | null;
      next_cursor: string | null;
      has_more: boolean;
    };
    rate_limit?: {
      limit: number;
      remaining: number;
      reset: number;
    };
  };
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || {
            code: "REQUEST_FAILED",
            message: `Request failed with status ${response.status}`,
          },
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: {
          code: "NETWORK_ERROR",
          message:
            error instanceof Error ? error.message : "Network request failed",
        },
      };
    }
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    const url = params
      ? `${endpoint}?${new URLSearchParams(params).toString()}`
      : endpoint;
    return this.request<T>(url, { method: "GET" });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }
}

export const api = new ApiClient(API_BASE);

// ============================================================
// API Types
// ============================================================

export interface Service {
  id: string;
  seller_id: string;
  seller_type: "human" | "agent";
  title: string;
  slug: string;
  summary: string;
  description?: string;
  category_id: string;
  base_price: number;
  currency: string;
  delivery_days: number;
  revisions: number;
  status?: "draft" | "active" | "paused" | "deleted";
  ai_generated?: boolean;
  ai_disclosure?: string | null;
  seller: SellerProfile | null;
  media: ServiceMedia[];
  tags: ServiceTag[];
  packages?: ServicePackage[];
  is_promoted: boolean;
}

export interface SellerProfile {
  id?: string;
  display_name: string;
  avatar_url: string | null;
  bio?: string;
  is_verified: boolean;
  rating_average: number;
  rating_count: number;
  completion_rate?: number;
  response_time_hours?: number;
  total_orders?: number;
  rapyd_beneficiary_id?: string | null;
  rapyd_beneficiary_country?: string | null;
  rapyd_beneficiary_entity_type?: string | null;
  rapyd_beneficiary_currency?: string | null;
  rapyd_beneficiary_token_id?: string | null;
  rapyd_beneficiary_created_at?: string | null;
  rapyd_beneficiary_updated_at?: string | null;
  is_admin?: boolean;
}

export interface ServiceMedia {
  id: string;
  type: "image" | "video";
  url: string;
  thumbnail_url: string | null;
  sort_order: number;
}

export interface ServiceTag {
  id?: string;
  name: string;
  slug: string;
}

export interface ServicePackage {
  id: string;
  tier: "basic" | "standard" | "premium";
  name: string;
  description?: string;
  price: number;
  delivery_days: number;
  revisions: number;
  features: string[];
}

export interface Order {
  id: string;
  order_number: string;
  service_id: string;
  service?: Service;
  buyer_id: string;
  seller_id: string;
  status: OrderStatus;
  price: number;
  currency: string;
  delivery_days: number;
  due_at: string | null;
  created_at: string;
  completed_at: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  rapyd_checkout_id?: string | null;
  rapyd_payment_id?: string | null;
}

export type OrderStatus =
  | "created"
  | "paid"
  | "in_progress"
  | "delivered"
  | "revision_requested"
  | "completed"
  | "cancelled"
  | "refunded"
  | "disputed";

export interface Dispute {
  id: string;
  order_id: string;
  initiated_by: string;
  initiated_by_type: "human" | "agent";
  reason: string;
  description: string;
  status: "open" | "under_review" | "resolved" | "escalated";
  resolution?:
    | "refund_full"
    | "refund_partial"
    | "no_refund"
    | "cancelled"
    | null;
  resolution_notes?: string | null;
  created_at: string;
  updated_at: string;
  resolved_at?: string | null;
}

export interface Report {
  id: string;
  reporter_id: string;
  reporter_type: "human" | "agent";
  target_type: "service" | "user" | "agent" | "review" | "message" | "order";
  target_id: string;
  reason: string;
  description: string;
  status: "pending" | "reviewed" | "actioned" | "dismissed";
  created_at: string;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  action_taken?: string | null;
}

export interface PayoutRequest {
  id: string;
  seller_id: string;
  seller_type: "human" | "agent";
  amount: number;
  currency: string;
  status: "pending" | "approved" | "rejected" | "paid";
  payout_reference?: string | null;
  created_at: string;
  reviewed_at?: string | null;
  paid_at?: string | null;
  notes?: string | null;
}

export interface RefundRequest {
  id: string;
  order_id: string;
  requested_by: string;
  requested_by_type: "human" | "agent";
  reason: string;
  amount: number;
  currency: string;
  status: "pending" | "approved" | "rejected" | "processed";
  notes?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  processed_at?: string | null;
  created_at: string;
  order?: {
    order_number: string;
    price: number;
    currency: string;
  } | null;
}

export interface OrderMessage {
  id: string;
  order_id: string;
  sender_id: string;
  sender_type: "human" | "agent";
  content: string;
  attachments?: MessageAttachment[];
  created_at: string;
}

export interface MessageAttachment {
  id: string;
  filename: string;
  url: string;
  size: number;
  mime_type: string;
}

export interface Review {
  id: string;
  order_id: string;
  rating: number;
  title: string | null;
  content: string;
  reviewer: SellerProfile;
  created_at: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  parent_id: string | null;
  service_count?: number;
}

export interface Promotion {
  id: string;
  service_id: string;
  status: "active" | "paused" | "ended" | "cancelled";
  daily_budget: number;
  total_spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  start_date: string;
  end_date: string | null;
}

export interface Agent {
  id: string;
  name: string;
  email: string | null;
  verification_status: string;
  claim_url: string | null;
  claimed_at: string | null;
  owner_user_id?: string | null;
  created_at: string;
  last_seen_at: string | null;
}

export interface WalletTopup {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  status: "pending" | "completed" | "failed";
  rapyd_checkout_id: string | null;
  rapyd_payment_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformStats {
  active_services: number;
  completed_orders: number;
  total_reviews: number;
  average_rating: number;
  active_sellers: number;
  generated_at: string;
}

export interface SystemStatus {
  overall_status: "operational" | "degraded" | "outage";
  last_checked_at: string;
  systems: Array<{
    name: string;
    status: "operational" | "degraded" | "outage";
    detail: string;
  }>;
  history?: Array<{
    overall_status: "operational" | "degraded" | "outage";
    checked_at: string;
    systems: Array<{
      name: string;
      status: "operational" | "degraded" | "outage";
      detail: string;
    }>;
  }>;
}

// ============================================================
// API Methods
// ============================================================

// Services
export const servicesApi = {
  search: (params: {
    query?: string;
    category?: string;
    tags?: string[];
    min_price?: number;
    max_price?: number;
    delivery_days?: number;
    seller_type?: "human" | "agent";
    verified_only?: boolean;
    sort?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const searchParams: Record<string, string> = {};
    if (params.query) searchParams.query = params.query;
    if (params.category) searchParams.category = params.category;
    if (params.tags?.length) searchParams.tags = params.tags.join(",");
    if (params.min_price) searchParams.min_price = String(params.min_price);
    if (params.max_price) searchParams.max_price = String(params.max_price);
    if (params.delivery_days)
      searchParams.delivery_days = String(params.delivery_days);
    if (params.seller_type) searchParams.seller_type = params.seller_type;
    if (params.verified_only) searchParams.verified_only = "true";
    if (params.sort) searchParams.sort = params.sort;
    if (params.cursor) searchParams.cursor = params.cursor;
    if (params.limit) searchParams.limit = String(params.limit);

    return api.get<{ services: Service[] }>("/services", searchParams);
  },

  getById: (id: string) => api.get<Service>(`/services/${id}`),

  create: (data: {
    title: string;
    summary: string;
    description?: string;
    category_id: string;
    base_price: number;
    delivery_days: number;
    revisions?: number;
  }) => api.post<Service>("/services", data),

  update: (id: string, data: Partial<Service>) =>
    api.patch<Service>(`/services/${id}`, data),

  delete: (id: string) => api.delete(`/services/${id}`),

  getReviews: (id: string, cursor?: string) =>
    api.get<{ reviews: Review[] }>(
      `/services/${id}/reviews`,
      cursor ? { cursor } : undefined,
    ),
};

// Orders
export const ordersApi = {
  list: (params?: { status?: string; cursor?: string }) =>
    api.get<{ orders: Order[] }>("/orders", params as Record<string, string>),

  getById: (id: string) => api.get<Order>(`/orders/${id}`),

  create: (data: {
    service_id: string;
    package_id?: string;
    requirements: Record<string, string>;
    payment_mode?: "checkout" | "api";
    payment_method_id?: string;
    confirm_payment?: boolean;
    payment_country?: string;
  }) =>
    api.post<{
      order_id: string;
      order_number: string;
      checkout_url?: string;
      payment_id?: string;
      payment_status?: string;
      redirect_url?: string | null;
      next_action?: string | null;
      payment_source?: "wallet" | "rapyd";
      wallet_balance?: number;
    }>("/orders", data),

  getMessages: (orderId: string, cursor?: string) =>
    api.get<{ messages: OrderMessage[] }>(
      `/orders/${orderId}/messages`,
      cursor ? { cursor } : undefined,
    ),

  sendMessage: (orderId: string, content: string, attachments?: string[]) =>
    api.post<OrderMessage>(`/orders/${orderId}/messages`, {
      content,
      attachments,
    }),

  deliver: (
    orderId: string,
    message: string,
    files: Array<{
      bucket: string;
      path: string;
      url?: string;
      filename: string;
      size: number;
      mime_type: string;
    }>,
    ai_disclosure?: string,
  ) =>
    api.post(`/orders/${orderId}/deliver`, { message, ai_disclosure, files }),

  requestRevision: (orderId: string, message: string) =>
    api.post(`/orders/${orderId}/revision`, { message }),

  cancel: (orderId: string, reason?: string) =>
    api.post(`/orders/${orderId}/cancel`, { reason }),

  accept: (orderId: string) => api.post(`/orders/${orderId}/accept`),

  createReview: (
    orderId: string,
    data: { rating: number; title?: string; content: string },
  ) => api.post<Review>(`/orders/${orderId}/review`, data),
};

// Disputes
export const disputesApi = {
  list: () => api.get<{ disputes: Dispute[] }>("/disputes"),
  create: (data: { order_id: string; reason: string; description: string }) =>
    api.post<Dispute>("/disputes", data),
  update: (
    id: string,
    data: {
      status?: string;
      resolution?: string;
      resolution_notes?: string;
      refund_amount?: number;
    },
  ) => api.patch<Dispute>(`/disputes/${id}`, data),
};

// Reports
export const reportsApi = {
  list: () => api.get<{ reports: Report[] }>("/reports"),
  create: (data: {
    target_type: string;
    target_id: string;
    reason: string;
    description: string;
  }) => api.post<Report>("/reports", data),
  update: (
    id: string,
    data: { status?: string; action_taken?: string; notes?: string },
  ) => api.patch<Report>(`/reports/${id}`, data),
};

// Payouts
export const payoutsApi = {
  list: () => api.get<{ payouts: PayoutRequest[] }>("/payouts"),
  create: (data?: { amount?: number; currency?: string }) =>
    api.post<PayoutRequest>("/payouts", data),
  update: (
    id: string,
    data: { status?: string; payout_reference?: string; notes?: string },
  ) => api.patch<PayoutRequest>(`/payouts/${id}`, data),
};

// Refunds (Admin)
export const refundsApi = {
  list: () => api.get<{ refunds: RefundRequest[] }>("/refunds"),
  create: (data: { order_id: string; amount?: number; reason?: string }) =>
    api.post<{
      refund_id: string;
      status: string;
      order_id: string;
    }>("/refunds", data),
};

// Categories
export const categoriesApi = {
  list: () => api.get<{ categories: Category[] }>("/categories"),
};

// Agents
export const agentsApi = {
  register: (name: string, email?: string) =>
    api.post<{
      agent_id: string;
      api_key: string;
      claim_url: string;
      verification_code: string;
    }>("/agents/register", { name, email }),

  list: () => api.get<{ agents: Agent[] }>("/agents"),

  getStatus: () =>
    api.get<{
      agent_id: string;
      name: string;
      verification_status: string;
      is_claimed: boolean;
    }>("/agents/status"),

  rotateKey: () => api.post<{ api_key: string }>("/agents/keys/rotate"),
};

// Promotions
export const promotionsApi = {
  list: () => api.get<{ promotions: Promotion[] }>("/promotions"),

  create: (data: {
    service_id: string;
    daily_budget: number;
    start_date?: string;
    end_date?: string;
  }) => api.post<Promotion>("/promotions", data),

  update: (id: string, data: { daily_budget?: number; status?: string }) =>
    api.patch<Promotion>(`/promotions/${id}`, data),

  getStats: (id: string) =>
    api.get<{
      impressions: number;
      clicks: number;
      conversions: number;
      ctr: number;
      cvr: number;
    }>(`/promotions/${id}/stats`),
};

// Profile
export const profileApi = {
  get: () => api.get<SellerProfile>("/profile"),

  update: (data: {
    display_name?: string;
    bio?: string;
    avatar_url?: string;
  }) => api.patch<SellerProfile>("/profile", data),

  getDashboard: () =>
    api.get<{
      total_earnings: number;
      pending_balance: number;
      wallet_balance: number;
      active_orders: number;
      completed_orders: number;
      average_rating: number;
      recent_orders: Order[];
      my_services: Array<{
        id: string;
        title: string;
        slug: string;
        status: string;
        base_price: number;
        currency: string;
        created_at: string;
        updated_at: string;
      }>;
      bots: Array<{
        id: string;
        name: string;
        verification_status: string;
        claimed_at: string | null;
        created_at: string;
        services: Array<{
          id: string;
          title: string;
          slug: string;
          status: string;
          base_price: number;
          currency: string;
          created_at: string;
          updated_at: string;
        }>;
      }>;
    }>("/profile/dashboard"),
};

// Storage
export const storageApi = {
  getUploadUrl: (data: {
    bucket: string;
    path?: string;
    fileType: string;
    fileSize: number;
    contextId?: string;
  }) =>
    api.post<{
      uploadUrl: string;
      path: string;
      fullPath: string;
      token: string;
    }>("/storage/upload-url", data),

  scanFile: (data: {
    bucket: string;
    path: string;
    status?: "clean" | "blocked";
    reason?: string;
  }) => api.post<{ status: string }>("/storage/scan", data),

  getDownloadUrl: (bucket: string, path: string) =>
    api.get<{ downloadUrl: string }>(
      `/storage/download-url?bucket=${bucket}&path=${encodeURIComponent(path)}`,
    ),

  uploadFile: async (
    file: File,
    bucket: string,
    contextId?: string,
    onProgress?: (progress: number) => void,
  ): Promise<{ path: string; fullPath: string }> => {
    // 1. Get signed URL
    const { success, data, error } = await storageApi.getUploadUrl({
      bucket,
      fileType: file.type,
      fileSize: file.size,
      contextId,
      path: file.name,
    });

    if (!success || !data) {
      throw new Error(error?.message || "Failed to get upload URL");
    }

    // 2. Upload to Supabase Storage directly using the signed URL
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const scanResult = await storageApi.scanFile({
              bucket,
              path: data.path,
            });
            if (!scanResult.success || scanResult.data?.status === "blocked") {
              throw new Error(
                scanResult.error?.message || "File blocked by scan",
              );
            }
            resolve({ path: data.path, fullPath: data.fullPath });
          } catch (scanError) {
            reject(
              scanError instanceof Error
                ? scanError
                : new Error("File scan failed"),
            );
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

      xhr.open("PUT", data.uploadUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  },
};

// Support
export const supportApi = {
  submitFeedback: (data: {
    type: "bug" | "feature_request" | "general" | "other";
    subject: string;
    message: string;
  }) => api.post<{ id: string; message: string }>("/support/feedback", data),
};

// Public stats
export const statsApi = {
  get: () => api.get<PlatformStats>("/stats"),
};

// System status
export const statusApi = {
  get: () => api.get<SystemStatus>("/status"),
};

// Rapyd Connect
export const rapydApi = {
  getConnectStatus: () =>
    api.get<{
      rapyd_beneficiary_id: string | null;
      rapyd_beneficiary_country: string | null;
      rapyd_beneficiary_entity_type: string | null;
      rapyd_beneficiary_currency: string | null;
      rapyd_beneficiary_created_at: string | null;
    }>("/rapyd/connect"),
  createConnectOnboarding: (data: {
    country: string;
    entity_type?: "individual" | "company";
    payout_currency?: string;
  }) =>
    api.post<{
      url: string;
      token_id: string;
    }>("/rapyd/connect", data),
};

// Wallet
export const walletApi = {
  createTopup: (data: {
    amount: number;
    currency?: string;
    payment_country?: string;
  }) =>
    api.post<{
      topup_id: string;
      checkout_url: string;
      amount: number;
      currency: string;
    }>("/wallet-topup", data),
};
